<template>
  <div>
    <h2 class="text-normal-emphasis font-bold mb-2">Hands</h2>

    <!-- Registration cap information (only shown when cap is set) -->
    <div v-if="pokerRun?.eventSettings.registrationCap" class="mb-4 p-2 rounded"
      :class="{ 'bg-blue-50': remainingRegistrationSlots > 5, 'bg-yellow-50': remainingRegistrationSlots <= 5 && remainingRegistrationSlots > 0, 'bg-red-50': remainingRegistrationSlots === 0 }">
      <p class="text-sm">
        <span v-if="remainingRegistrationSlots > 0">
          {{ remainingRegistrationSlots }} {{ remainingRegistrationSlots === 1 ? 'hand' : 'hands' }} available
          <span v-if="totalHandsSelected > 0"> | Selected: {{ totalHandsSelected }}</span>
        </span>
        <span v-else class="font-bold">
          No more hands available for this event
        </span>
      </p>
    </div>

    <div class="mb-6">
      <template v-for="(registration, index) in registrationItems" :key="registration.item.itemId">
        <!-- Standard Hand -->
        <LineItemControl :item="registration.item" v-model="registration.count" :quantity-options="registrationOptions"
          :name="`registration-${index}`" :show-quantity-selector="registration.count > 1"
          :disable-add="remainingRegistrationSlots === 0" />

        <p v-if="registration.count > 0" class="text-sm text-normal-reduced mb-1">
          If selecting more than one hand, you can assign them to other people after check out.
        </p>
        <p v-if="registration.count > 0" class="text-sm text-normal-reduced mb-4">
          You may purchase extra cards until the third card is dealt.
        </p>

        <!-- Extra Cards Section -->
        <template v-if="pokerRun?.eventSettings.enableExtraCard">
          <LineItemControl :item="getExtraCardItem(registration.item)" v-model="registration.countWithExtraCard"
            :quantity-options="registrationOptions" :name="`registration-extra-${index}`" :is-extra-card="true"
            :show-quantity-selector="registration.countWithExtraCard > 1"
            :disable-add="remainingRegistrationSlots === 0 || totalHandsSelected >= remainingRegistrationSlots" />
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import Constants from "@/models/Constants";
import { Item } from "@models/Item";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { computed, ref, watch } from "vue";
import { SelectOption } from '@/models/SelectOption';
import LineItemControl from "@/components/LineItemControl.vue";

interface RegistrationItem {
  item: Item;
  count: number;
  countWithExtraCard: number;
}

interface Props {
  pokerRun: PokerRunEvent | undefined;
  isProcessing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isProcessing: false
});
const emit = defineEmits(['update:registrationItems']);

// Calculate the remaining registration slots based on the registration cap
const remainingRegistrationSlots = computed(() => {
  if (!props.pokerRun || !props.pokerRun.eventSettings.registrationCap) {
    // If no cap is set, return 10 as the default max
    return 99;
  }

  const registrationCap = props.pokerRun.eventSettings.registrationCap;
  const currentRegistrationCount = props.pokerRun._internal?.registrationCount || 0;

  // Calculate remaining slots
  const remaining = registrationCap - currentRegistrationCount;

  // Ensure we don't return a negative number
  return Math.max(0, remaining);
});

// Dynamically generate quantity options based on the remaining slots
const registrationOptions = computed(() => {

  const maxOption = Math.min(remainingRegistrationSlots.value, 99);
  const options: SelectOption[] = Array.from({ length: maxOption }, (_, index) => ({
    id: index + 1,
    name: (index + 1).toString()
  }));

  // Log options for debugging
  console.log('Registration options:', options);
  console.log('Max option:', maxOption);
  console.log('Remaining slots:', remainingRegistrationSlots.value);

  return options;
});

const registrationItems = ref<RegistrationItem[]>([]);

const extraCardLineItem = computed(() => {
  if (!props.pokerRun) return null;
  return props.pokerRun.lineItems.find(x => x.itemType === Constants.EXTRA_CARD_TYPE);
});

// Function to create an item that represents a hand with an extra card
const getExtraCardItem = (baseItem: Item): Item => {
  const extraCard = extraCardLineItem.value;
  const combinedAmount = baseItem.amount + (extraCard?.amount || 0);

  return {
    ...baseItem,
    name: `${baseItem.name} + Extra Card`,
    amount: combinedAmount,
    itemId: `${baseItem.itemId}-extra`
  };
};

const initializeRegistrationItems = () => {
  if (!props.pokerRun) return;

  const feeItems = props.pokerRun.lineItems.filter(x => x.itemType === Constants.FEE_TYPE);
  registrationItems.value = feeItems.map(item => ({
    item,
    count: 0,
    countWithExtraCard: 0
  }));
};

// Calculate the total number of hands selected
const totalHandsSelected = computed(() => {
  return registrationItems.value.reduce((total, item) => {
    return total + item.count + item.countWithExtraCard;
  }, 0);
});

// Watch for changes to ensure we don't exceed the registration cap
watch(totalHandsSelected, (newTotal) => {
  if (props.pokerRun?.eventSettings.registrationCap) {
    const maxAllowed = remainingRegistrationSlots.value;

    if (newTotal > maxAllowed) {
      console.warn(`Selected hands (${newTotal}) exceed available slots (${maxAllowed})`);

      // Instead of resetting all quantities, adjust them to fit within the cap
      let excess = newTotal - maxAllowed;

      // First reduce extra card registrations
      for (let i = 0; i < registrationItems.value.length && excess > 0; i++) {
        const item = registrationItems.value[i];
        if (item.countWithExtraCard > 0) {
          const reduction = Math.min(item.countWithExtraCard, excess);
          item.countWithExtraCard -= reduction;
          excess -= reduction;
        }
      }

      // Then reduce standard registrations if needed
      for (let i = 0; i < registrationItems.value.length && excess > 0; i++) {
        const item = registrationItems.value[i];
        if (item.count > 0) {
          const reduction = Math.min(item.count, excess);
          item.count -= reduction;
          excess -= reduction;
        }
      }
    }
  }
});

watch(() => props.pokerRun, (newVal) => {
  // Only initialize if not currently processing
  if (newVal && !props.isProcessing) {
    initializeRegistrationItems();
  }
}, { immediate: true });

watch(() => registrationItems.value, (newVal) => {
  emit('update:registrationItems', newVal);
}, { deep: true });
</script>
